/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ApprovalRequestType, Properties, Recipient, Role, RoleStatus, Status } from '../../../data/types.generated';
import { ApprovalResponseType, ApprovalViewProperties } from '../../../service/gen';

import { ApprovalType } from '../../../service/gen/esign';
import { SharePointESignStatus } from '../../../utilities/EsignUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { InfoTagComponentProps, TagColorStyle } from './InfoTag.component';

const defaultInfoTagProps: InfoTagComponentProps = {
    text: '',
    colorStyle: TagColorStyle.Request,
    providerStatus: '',
};

export function computeInfoTagPropsFromApprovalViewProperties(
    properties: ApprovalViewProperties
): InfoTagComponentProps {
    if (!properties) {
        return defaultInfoTagProps;
    }
    const { IsActive, Result, Type } = properties;
    const IsCustomResponse =
        Type === ApprovalResponseType.CustomResponse || Type === ApprovalResponseType.CustomResponseAwaitAll;
    if (properties.ApprovalType != null && properties.ApprovalType === ApprovalType.ESign) {
        return computeInfoTagEsign(Result, properties);
    } else {
        return computeInfoTagFlow(IsActive, Result, IsCustomResponse);
    }
}

export function computeInfoTagPropsFromProperties(
    properties: Properties,
    requestType?: ApprovalRequestType
): InfoTagComponentProps {
    if (!properties) {
        return defaultInfoTagProps;
    }
    const { isActive, result, type } = properties;
    const isCustomResponse =
        type === ApprovalResponseType.CustomResponse || type === ApprovalResponseType.CustomResponseAwaitAll;

    if (properties?.approvalType === ApprovalType.ESign) {
        return computeInfoTagEsign(result);
    } else {
        return computeInfoTagFlow(isActive, result, isCustomResponse, requestType);
    }
}

/**
 * Text and color to show on the UI for the Approval status.
 * Mapping Gateway API status to Approvals status.
 *
 * @param status the status of the ESign Approval type.
 * @returns InfoTagComponentProps - the color and status to show for the UI.
 */
export function computeInfoTagForESignApprovals(status: Status, user?: Recipient): InfoTagComponentProps {
    const loc = getAppHost().getLocale();

    switch (status) {
        case Status.Declined: // declined
            return { colorStyle: TagColorStyle.Reject, text: loc.strings.declined };
        case Status.Voided: // voided
            return { colorStyle: TagColorStyle.Cancel, text: loc.strings.canceled };
        case Status.Signed: // signed
            return { colorStyle: TagColorStyle.Approve, text: loc.strings.signed };
        case Status.Sent:
            // if the request is waiting for approval of the current user,
            // the status should be "needs action"
            if (user?.role === Role.Signer && user.roleStatus !== RoleStatus.Signed) {
                return { colorStyle: TagColorStyle.Request, text: loc.strings.needsAction };
            }
    }

    return {
        colorStyle: TagColorStyle.Request,
        text: loc.strings.inProgress,
    };
}

function computeInfoTagEsign(result: string, properties?: ApprovalViewProperties): InfoTagComponentProps {
    const loc = getAppHost().getLocale();

    const infoTagOut: InfoTagComponentProps = {
        colorStyle: TagColorStyle.Request,
        text: loc.strings.inProgress,
    };

    if (result === 'NeedsAction') {
        infoTagOut.colorStyle = TagColorStyle.Request;
        infoTagOut.text = loc.strings.needsAction;
    } else if (result === 'InProgress') {
        infoTagOut.colorStyle = TagColorStyle.Request;
        infoTagOut.text = loc.strings.inProgress;
    } else if (result === 'Approved') {
        infoTagOut.colorStyle = TagColorStyle.Approve;
        infoTagOut.text = loc.strings.approved;
    } else if (result === 'Signed') {
        infoTagOut.colorStyle = TagColorStyle.Approve;
        infoTagOut.text = loc.strings.signed;
    } else if (result === 'Complete') {
        infoTagOut.colorStyle = TagColorStyle.Approve;
        infoTagOut.text = loc.strings.complete;
    } else if (result === 'Canceled') {
        infoTagOut.colorStyle = TagColorStyle.Cancel;
        infoTagOut.text = loc.strings.canceled;
    } else if (result === 'Rejected') {
        infoTagOut.text = loc.strings.declined;
        infoTagOut.colorStyle = TagColorStyle.Reject;
    } else {
        infoTagOut.colorStyle = TagColorStyle.Request;
        infoTagOut.text = loc.strings.requested;
    }

    if (properties.AdditionalViewProperties?.ProviderStatus === SharePointESignStatus.PendingCompletion) {
        infoTagOut.providerStatus = loc.strings.pending_completion;
    }
    return infoTagOut;
}

function computeInfoTagFlow(
    isActive: boolean,
    result: string,
    isCustomResponse: boolean,
    requestType?: ApprovalRequestType
): InfoTagComponentProps {
    const loc = getAppHost().getLocale();

    // For the Agent and app requests (Collab Manager) provider, an active/open request is
    // labelled "Pending" instead of the shared "Requested" status used by other providers.
    const activeText =
        requestType === ApprovalRequestType.AgentAndAppApprovals
            ? loc.strings.agentapp_status_pending
            : loc.strings.requested;

    let text: string = activeText;
    let colorStyle: TagColorStyle = TagColorStyle.Request;
    if (result != null) {
        const resultArray = result.split('|');
        if (resultArray.length > 0) {
            result = resultArray[resultArray.length - 1];
        }
    }

    if (isActive) {
        text = activeText;
        colorStyle = TagColorStyle.Request;
    } else if (result === 'Canceled') {
        text = loc.strings.canceled;
        colorStyle = TagColorStyle.Cancel;
    } else if (isCustomResponse) {
        text = loc.strings.completed;
        if (result) {
            const responses = new Set(result.split(';'));
            if (responses.size === 1) {
                text = responses.values().next().value;
            }
        }
        colorStyle = TagColorStyle.CustomResponse;
    } else if (result === 'Reject') {
        text = loc.strings.rejected;
        colorStyle = TagColorStyle.Reject;
    } else if (result === 'Approve') {
        text = loc.strings.approved;
        colorStyle = TagColorStyle.Approve;
    }

    return {
        text,
        colorStyle,
    };
}
